import Splide from "@splidejs/splide";

// Константы селекторов и классов
const SELECTORS = {
  SPLIDE: "splide",
  SPLIDE_TRACK: "splide__track",
  SPLIDE_LIST: "splide__list",
  SPLIDE_SLIDE: "splide__slide",
};

/**
 * Класс для управления слайдерами на основе библиотеки Splide.js
 *
 * SliderManager предоставляет гибкий интерфейс для создания и управления слайдерами
 * с поддержкой адаптивности, динамического создания/уничтожения и кастомной обертки DOM.
 *
 * Основные возможности:
 * - Автоматическое создание слайдера при инициализации
 * - Адаптивное поведение с поддержкой условий создания
 * - Динамическая обертка DOM элементов для Splide
 * - Глобальное хранение экземпляров слайдеров
 * - Обработка изменения размеров окна
 *
 * @example
 * // Базовое использование
 * const slider = new SliderManager({
 *   selector: '.my-slider',
 *   splideOptions: {
 *     perPage: 3,
 *     pagination: true
 *   }
 * });
 * slider.init();
 *
 * @example
 * // С условием создания (только на мобильных)
 * const mobileSlider = new SliderManager({
 *   selector: '.mobile-slider',
 *   splideOptions: { perPage: 1 },
 *   condition: () => window.innerWidth < 768,
 *   globalVarName: 'mobileSlider'
 * });
 * mobileSlider.init();
 *
 * @example
 * // С динамической оберткой DOM
 * const dynamicSlider = new SliderManager({
 *   selector: '.dynamic-slider',
 *   splideOptions: { perPage: 2 },
 *   dynamicWrapper: {
 *     trackClass: 'slider-track',
 *     itemClass: 'slider-item'
 *   }
 * });
 * dynamicSlider.init();
 */
export default class SliderManager {
  /**
   * Создает новый экземпляр SliderManager
   *
   * Конструктор принимает конфигурационный объект с параметрами,
   * которые определяют поведение слайдера и условия его создания.
   *
   * @param {Object} config - Конфигурационный объект
   * @param {string} config.selector - CSS селектор контейнера слайдера (обязательный)
   *   Должен быть валидным CSS селектором, который будет использоваться для поиска
   *   DOM элемента через document.querySelector().
   *   Пример: '.advantages__list', '#main-slider', '[data-slider]'
   *
   * @param {Object} config.splideOptions - Параметры конфигурации Splide.js (обязательный)
   *   Полный набор опций передается напрямую в конструктор Splide.
   *   Включает настройки отображения, навигации, анимации и т.д.
   *   Подробную документацию по опциям см. в документации Splide.js
   *
   * @param {Function|null} [config.createCondition=null] - Функция-условие создания слайдера
   *   Должна возвращать boolean значение, определяющее нужно ли создавать слайдер.
   *   Вызывается при инициализации и при изменении размера окна.
   *   Если не указана, слайдер создается всегда.
   *   Пример: () => window.innerWidth < 768 (создать только на мобильных)
   *
   * @param {string|null} [config.globalVarName=null] - Имя глобальной переменной для хранения слайдера
   *   Если указано, экземпляр Splide будет сохранен в window[имя].
   *   Полезно для отладки и внешнего доступа к слайдеру.
   *   Пример: 'advantagesSlider' - будет доступно как window.advantagesSlider
   *
   * @param {number} [config.resizeDelay=250] - Задержка в миллисекундах перед пересозданием слайдера
   *   При изменении размера окна слайдер уничтожается и создается заново.
   *   Задержка предотвращает частые пересоздания при быстром изменении размера.
   *   Рекомендуемые значения: 100-500ms
   *
   * @param {Function|null} [config.condition=null] - Устаревший параметр, используйте createCondition
   *   Оставлен для обратной совместимости. Если указан и createCondition не задан,
   *   будет использоваться как createCondition.
   *
   * @param {Object|null} [config.dynamicWrapper=null] - Настройки динамической обертки DOM
   *   Позволяет автоматически обернуть существующие элементы в структуру Splide.
   *   Используется когда HTML не соответствует требованиям Splide изначально.
   *
   * @param {string} config.dynamicWrapper.trackClass - CSS класс контейнера трека
   *   Элемент с этим классом будет использоваться как splide__track.
   *   Должен содержать дочерние элементы с классом itemClass.
   *
   * @param {string} config.dynamicWrapper.itemClass - CSS класс элементов слайдов
   *   Дочерние элементы с этим классом будут преобразованы в splide__slide.
   *   Фильтруются только элементы с указанным классом.
   *
   * @example
   * // Полная конфигурация с всеми параметрами
   * const slider = new SliderManager({
   *   selector: '.advantages__list',
   *   splideOptions: {
   *     perPage: 1,
   *     perMove: 1,
   *     pagination: true,
   *     arrows: false,
   *     padding: { right: '13.3%' },
   *     focus: 'center',
   *     trimSpace: false,
   *     updateOnMove: true,
   *     waitForTransition: true,
   *     gap: '24px'
   *   },
   *   createCondition: () => window.innerWidth < 768,
   *   globalVarName: 'advantagesSlider',
   *   resizeDelay: 250,
   *   dynamicWrapper: {
   *     trackClass: 'advantages__slider-track',
   *     itemClass: 'advantages__item'
   *   }
   * });
   */
  constructor({
    selector,
    splideOptions,
    createCondition = null,
    globalVarName = null,
    resizeDelay = 250,
    condition = null,
    dynamicWrapper = null,
  }) {
    this.selector = selector;
    this.splideOptions = splideOptions;
    this.createCondition = createCondition || condition;
    this.globalVarName = globalVarName;
    this.resizeDelay = resizeDelay;
    this.dynamicWrapper = dynamicWrapper;

    this.element = null;
    this.slider = null;
    this.resizeTimer = null;
    this.originalSlides = null;
  }

  /**
   * Свойства экземпляра класса
   * @property {string} selector - CSS селектор контейнера слайдера
   * @property {Object} splideOptions - Конфигурационные параметры Splide.js
   * @property {Function|null} createCondition - Функция-условие создания слайдера
   * @property {string|null} globalVarName - Имя глобальной переменной для хранения слайдера
   * @property {number} resizeDelay - Задержка перед пересозданием слайдера при resize
   * @property {Object|null} dynamicWrapper - Настройки динамической обертки DOM
   * @property {HTMLElement|null} element - Найденный DOM элемент контейнера
   * @property {Splide|null} slider - Экземпляр Splide слайдера
   * @property {number|null} resizeTimer - ID таймера для debounce resize события
   * @property {NodeList|null} originalSlides - Сохраненные оригинальные элементы слайдов
   */

  /**
   * Инициализирует слайдер
   *
   * Основной метод, который необходимо вызвать после создания экземпляра.
   * Выполняет поиск DOM элемента, создает слайдер и добавляет обработчик изменения размера окна.
   *
   * Процесс инициализации:
   * 1. Ищет DOM элемент по указанному селектору
   * 2. Если элемент найден - создает слайдер
   * 3. Добавляет обработчик события resize для адаптивности
   *
   * @returns {void}
   *
   * @example
   * const slider = new SliderManager({ selector: '.my-slider', splideOptions: {} });
   * slider.init(); // Запускает процесс инициализации
   *
   * @example
   * // Если элемент не найден - выводит предупреждение в консоль
   * const missingSlider = new SliderManager({ selector: '.non-existent', splideOptions: {} });
   * missingSlider.init(); // console.warn: "Slider element not found: .non-existent"
   */
  init() {
    this.element = document.querySelector(this.selector);

    if (!this.element) {
      console.warn(`Slider element not found: ${this.selector}`);

      return;
    }

    this.createSlider();
    this.addResizeListener();
  }

  /**
   * Создает экземпляр Splide слайдера
   *
   * Основной метод создания слайдера, который:
   * 1. Уничтожает существующий слайдер (если есть)
   * 2. Проверяет условие создания через shouldCreateSlider()
   * 3. Применяет динамическую обертку DOM (если настроена)
   * 4. Создает новый экземпляр Splide с указанными опциями
   * 5. Сохраняет слайдер в глобальную переменную (если указано)
   *
   * Метод вызывается автоматически при инициализации и при изменении размера окна.
   * Может быть вызван вручную для принудительного пересоздания слайдера.
   *
   * @returns {void}
   *
   * @example
   * // Принудительное пересоздание слайдера
   * slider.createSlider();
   *
   * @example
   * // Метод вызывается автоматически при resize
   * // через addResizeListener -> setTimeout -> createSlider
   */
  createSlider() {
    this.destroySlider();

    if (!this.shouldCreateSlider()) {
      return;
    }

    // Оборачивание DOM для Splide при использовании dynamicWrapper
    if (this.dynamicWrapper && this.prepareDynamicWrapper()) {
      // proceed after wrapping
    }

    this.slider = new Splide(this.element, this.splideOptions).mount();

    if (this.globalVarName) {
      window[this.globalVarName] = this.slider;
    }
  }

  /**
   * Уничтожает существующий слайдер и очищает связанные ресурсы
   *
   * Метод выполняет полную очистку слайдера:
   * 1. Вызывает метод destroy() у экземпляра Splide (если существует)
   * 2. Очищает ссылку на слайдер в классе
   * 3. Удаляет ссылку из глобальной переменной window (если была установлена)
   * 4. Восстанавливает оригинальную структуру DOM при использовании dynamicWrapper
   *
   * Восстановление DOM при dynamicWrapper включает:
   * - Удаление классов splide и splide__track
   * - Восстановление оригинальных элементов слайдов
   * - Очистку созданного контейнера splide__list
   *
   * @returns {void}
   *
   * @example
   * // Ручное уничтожение слайдера
   * slider.destroySlider();
   *
   * @example
   * // Автоматическое уничтожение при resize
   * // createSlider() -> destroySlider() -> новая инициализация
   *
   * @example
   * // Восстановление оригинального DOM после уничтожения
   * // Было: <div class="advantages__slider-track splide__track">
   * //         <div class="splide__list">...</div>
   * //       </div>
   * // Стало: <div class="advantages__slider-track">
   * //         <div class="advantages__item">...</div>
   * //         <div class="advantages__item">...</div>
   * //       </div>
   */
  destroySlider() {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }

    if (this.globalVarName && window[this.globalVarName]) {
      window[this.globalVarName] = null;
    }

    // Удаление обертки при dynamicWrapper
    if (this.dynamicWrapper && this.element) {
      const { trackClass } = this.dynamicWrapper;
      const track = this.element.querySelector(`.${trackClass}`);
      const list = track?.querySelector(`.${SELECTORS.SPLIDE_LIST}`);

      if (list && this.originalSlides) {
        track.innerHTML = "";
        this.originalSlides.forEach((el) => track.appendChild(el));
        this.originalSlides = null;
        track.classList.remove(SELECTORS.SPLIDE_TRACK);
      }

      this.element.classList.remove(SELECTORS.SPLIDE);
    }
  }

  /**
   * Подготавливает DOM структуру для работы с Splide путем динамической обертки
   *
   * Метод преобразует существующую HTML структуру в формат, требуемый Splide.js.
   * Используется когда исходный HTML не соответствует требованиям Splide изначально.
   *
   * Процесс преобразования:
   * 1. Находит контейнер трека по классу trackClass
   * 2. Добавляет необходимые классы Splide (splide, splide__track)
   * 3. Создает контейнер splide__list
   * 4. Фильтрует дочерние элементы по классу itemClass
   * 5. Сохраняет оригинальные элементы для возможности восстановления
   * 6. Добавляет класс splide__slide к элементам и перемещает их в splide__list
   *
   * @returns {boolean} true если преобразование прошло успешно, false если контейнер не найден
   *
   * @example
   * // Исходная структура HTML
   * <div class="advantages__list">
   *   <div class="advantages__slider-track">
   *     <div class="advantages__item">Слайд 1</div>
   *     <div class="advantages__item">Слайд 2</div>
   *     <div class="advantages__item">Слайд 3</div>
   *   </div>
   * </div>
   *
   * @example
   * // После преобразования
   * <div class="advantages__list splide">
   *   <div class="advantages__slider-track splide__track">
   *     <div class="splide__list">
   *       <div class="advantages__item splide__slide">Слайд 1</div>
   *       <div class="advantages__item splide__slide">Слайд 2</div>
   *       <div class="advantages__item splide__slide">Слайд 3</div>
   *     </div>
   *   </div>
   * </div>
   *
   * @example
   * // Использование в конфигурации
   * const slider = new SliderManager({
   *   selector: '.advantages__list',
   *   dynamicWrapper: {
   *     trackClass: 'advantages__slider-track',
   *     itemClass: 'advantages__item'
   *   }
   * });
   */
  prepareDynamicWrapper() {
    const { trackClass, itemClass } = this.dynamicWrapper;
    const track = this.element.querySelector(`.${trackClass}`);

    if (!track) return false;

    this.element.classList.add(SELECTORS.SPLIDE);
    track.classList.add(SELECTORS.SPLIDE_TRACK);

    const list = document.createElement("div");

    list.className = SELECTORS.SPLIDE_LIST;

    const children = Array.from(track.children).filter((el) =>
      el.classList.contains(itemClass),
    );

    this.originalSlides = children.map((el) => el.cloneNode(true)); // Save original

    children.forEach((el) => {
      el.classList.add(SELECTORS.SPLIDE_SLIDE);
      list.appendChild(el);
    });

    track.innerHTML = "";
    track.appendChild(list);

    return true;
  }

  /**
   * Проверяет условие создания слайдера
   *
   * Метод определяет нужно ли создавать слайдер в текущий момент.
   * Используется для реализации адаптивного поведения и условного создания слайдеров.
   *
   * Логика работы:
   * - Если функция createCondition задана - вызывает ее и возвращает результат
   * - Если createCondition не задана - возвращает true (создать всегда)
   *
   * @returns {boolean} true если слайдер нужно создать, false если нет
   *
   * @example
   * // Без условия - слайдер создается всегда
   * shouldCreateSlider() // true
   *
   * @example
   * // С условием создания только на мобильных
   * createCondition: () => window.innerWidth < 768
   * // На экране шириной 320px: shouldCreateSlider() // true
   * // На экране шириной 1024px: shouldCreateSlider() // false
   *
   * @example
   * // Сложное условие с несколькими факторами
   * createCondition: () => {
   *   return window.innerWidth < 768 && document.querySelectorAll('.slide').length > 1;
   * }
   */
  shouldCreateSlider() {
    if (this.createCondition) {
      return this.createCondition();
    }

    return true;
  }

  /**
   * Добавляет обработчик события изменения размера окна
   *
   * Метод реализует адаптивное поведение слайдера путем пересоздания
   * при изменении размера окна браузера. Использует debounce паттерн
   * для предотвращения частых пересозданий слайдера.
   *
   * Процесс работы:
   * 1. Добавляет обработчик события 'resize' на window
   * 2. При каждом изменении размера очищает предыдущий таймер
   * 3. Устанавливает новый таймер с задержкой resizeDelay
   * 4. По истечении задержки вызывает createSlider() для пересоздания
   *
   * Преимущества debounce:
   * - Предотвращает множественные пересоздания при быстром изменении размера
   * - Снижает нагрузку на браузер и улучшает производительность
   * - Обеспечивает стабильное состояние после завершения изменения размера
   *
   * @returns {void}
   *
   * @example
   * // Автоматическая адаптация при resize
   * // Пользователь тянет за край окна браузера
   * // Через 250ms (по умолчанию) слайдер пересоздается
   *
   * @example
   * // Изменение задержки для быстрой реакции
   * const slider = new SliderManager({
   *   selector: '.slider',
   *   resizeDelay: 100, // Быстрая реакция
   *   createCondition: () => window.innerWidth < 768
   * });
   *
   * @example
   * // Изменение задержки для снижения нагрузки
   * const slider = new SliderManager({
   *   selector: '.slider',
   *   resizeDelay: 500, // Медленная реакция, меньше нагрузки
   *   createCondition: () => window.innerWidth < 768
   * });
   */
  addResizeListener() {
    window.addEventListener("resize", () => {
      clearTimeout(this.resizeTimer);

      this.resizeTimer = setTimeout(() => {
        this.createSlider();
      }, this.resizeDelay);
    });
  }
}
